import { useCallback, useRef } from 'react';
import { Invitation, SessionState } from 'sip.js';
import { sipDestinationUri } from '@/services/sip/sipAccount.defaults';
import { buildSupervisorDialString, } from '@/services/sip/supervisorCallActions';
import { ANSWER_INVITE_OPTIONS, createOutboundProgressTracker, createReferAwaitOptions, handleOutboundEarlyMediaProgress, incomingRingFromSession, isLineBusyRejectStatus, mapSipMediaException, outboundPhaseFromProgress, OUTBOUND_INVITER_OPTIONS, sipResponseLine, } from './sipCallHelpers';
export function useSipProviderActions(deps) {
    const { account, sipAudioElement, mgrRef, incomingRing, conversationUi, incomingRingRef, sipStatusRef, conversationUiRef, conversationSessionRef, consultSessionRef, warmConsultDialAllowedRef, warmSkipRecoverOnConsultHangupRef, lastOutboundDialRef, subscribeConversationPhaseFnRef, unsubscribeConversationPhaseFnRef, attachRingTerminatedListener, detachRingTerminatedListener, setSipError, setIncomingRing, setConversationUi, setSipMediaUiEpoch, setWarmTransfer, } = deps;
    const markOutboundPhase = useCallback((phase) => {
        setConversationUi((current) => current?.direction === 'outbound' ? { ...current, outboundPhase: phase } : current);
    }, [setConversationUi]);
    const outboundProgressRef = useRef(createOutboundProgressTracker());
    const dial = useCallback(async (rawNumber) => {
        const sm = mgrRef.current;
        if (!sm) {
            throw new Error('Softphone not ready.');
        }
        if (!sm.isConnected()) {
            throw new Error('SIP disconnected. Check WebSocket URL and PBX.');
        }
        if (sipStatusRef.current !== 'registered') {
            try {
                await sm.register();
            }
            catch {
                /* Some PBXs still accept outbound INVITE when the WebSocket is up. */
            }
        }
        const blockingSecondInvite = conversationUiRef.current?.sipState === SessionState.Established &&
            !warmConsultDialAllowedRef.current;
        if (blockingSecondInvite) {
            throw new Error('Cannot start another outbound invite while this call is active (use Warm Transfer).');
        }
        let destUri;
        try {
            destUri = sipDestinationUri(rawNumber, account);
        }
        catch {
            throw new Error('Enter a number.');
        }
        lastOutboundDialRef.current = rawNumber.trim();
        setSipError(null);
        outboundProgressRef.current = createOutboundProgressTracker();
        try {
            await sm.call(destUri, OUTBOUND_INVITER_OPTIONS, {
                requestDelegate: {
                    onProgress: (response) => {
                        handleOutboundEarlyMediaProgress(response, conversationSessionRef.current, sipAudioElement);
                        const phase = outboundPhaseFromProgress(response.message?.statusCode, outboundProgressRef.current);
                        if (phase) {
                            markOutboundPhase(phase);
                        }
                    },
                    onReject: (response) => {
                        if (isLineBusyRejectStatus(response.message?.statusCode)) {
                            markOutboundPhase('line-busy');
                        }
                        setSipError(sipResponseLine('Outgoing call rejected', response));
                    },
                },
            });
        }
        catch (e) {
            setSipError(mapSipMediaException(e));
        }
    }, [
        account,
        sipAudioElement,
        mgrRef,
        sipStatusRef,
        conversationUiRef,
        conversationSessionRef,
        warmConsultDialAllowedRef,
        lastOutboundDialRef,
        markOutboundPhase,
        setSipError,
    ]);
    const declineIncoming = useCallback(async () => {
        const sm = mgrRef.current;
        const ring = incomingRing;
        if (!sm || !ring)
            return;
        detachRingTerminatedListener(ring.session);
        await sm.decline(ring.session).catch(() => undefined);
        setIncomingRing(null);
    }, [incomingRing, detachRingTerminatedListener, mgrRef, setIncomingRing]);
    const answerIncoming = useCallback(async () => {
        const sm = mgrRef.current;
        const ring = incomingRing;
        if (!sm || !ring)
            return;
        const session = ring.session;
        if (!(session instanceof Invitation))
            return;
        conversationSessionRef.current = session;
        subscribeConversationPhaseFnRef.current(session);
        setIncomingRing(null);
        setSipError(null);
        try {
            await sm.answer(session, ANSWER_INVITE_OPTIONS);
        }
        catch (e) {
            setSipError(mapSipMediaException(e));
            unsubscribeConversationPhaseFnRef.current();
            conversationSessionRef.current = null;
            if (session.state === SessionState.Initial) {
                attachRingTerminatedListener(session);
                setIncomingRing({
                    session,
                    ...incomingRingFromSession(session),
                });
            }
        }
    }, [
        incomingRing,
        attachRingTerminatedListener,
        mgrRef,
        conversationSessionRef,
        subscribeConversationPhaseFnRef,
        unsubscribeConversationPhaseFnRef,
        setIncomingRing,
        setSipError,
    ]);
    const hangupActiveOrDeclineIncoming = useCallback(async () => {
        const sm = mgrRef.current;
        if (!sm)
            return;
        const consultLeg = consultSessionRef.current;
        if (consultLeg) {
            await sm.hangup(consultLeg).catch(() => undefined);
        }
        if (incomingRing) {
            await declineIncoming();
            return;
        }
        const active = conversationSessionRef.current;
        if (active) {
            unsubscribeConversationPhaseFnRef.current();
            await sm.hangup(active).catch(() => undefined);
        }
        setWarmTransfer({ phase: 'idle', destinationLabel: null });
    }, [incomingRing, declineIncoming, mgrRef, consultSessionRef, unsubscribeConversationPhaseFnRef, setWarmTransfer]);
    const toggleMute = useCallback(() => {
        const sm = mgrRef.current;
        const session = conversationSessionRef.current;
        if (!sm || !session || conversationUi?.sipState !== SessionState.Established)
            return;
        if (sm.isMuted(session))
            sm.unmute(session);
        else
            sm.mute(session);
        setSipMediaUiEpoch((e) => e + 1);
    }, [conversationUi?.sipState, mgrRef, conversationSessionRef, setSipMediaUiEpoch]);
    const toggleHold = useCallback(async () => {
        const sm = mgrRef.current;
        const session = conversationSessionRef.current;
        if (!sm || !session || conversationUi?.sipState !== SessionState.Established)
            return;
        try {
            if (sm.isHeld(session))
                await sm.unhold(session);
            else
                await sm.hold(session);
        }
        catch {
            /* re-INVITE may fail silently in edge cases — UI follows session state */
        }
        setSipMediaUiEpoch((e) => e + 1);
    }, [conversationUi?.sipState, mgrRef, conversationSessionRef, setSipMediaUiEpoch]);
    const transferActiveCall = useCallback(async (rawNumber) => {
        const sm = mgrRef.current;
        const session = conversationSessionRef.current;
        if (!sm) {
            throw new Error('Softphone not ready.');
        }
        if (!sm.isConnected()) {
            throw new Error('SIP disconnected. Cannot transfer.');
        }
        if (!session || conversationUi?.sipState !== SessionState.Established) {
            throw new Error('Answer the call before transferring.');
        }
        if (consultSessionRef.current) {
            throw new Error('Complete or cancel the warm transfer consult before a cold transfer.');
        }
        let destUri;
        try {
            destUri = sipDestinationUri(rawNumber, account);
        }
        catch {
            throw new Error('Enter a destination number or extension.');
        }
        setSipError(null);
        const referAwait = createReferAwaitOptions();
        try {
            await sm.transfer(session, destUri, {
                onNotify: referAwait.onNotify,
                requestDelegate: {
                    onReject: (response) => {
                        const line = sipResponseLine('Transfer rejected', response);
                        referAwait.cancelReferWait(line);
                        void setSipError(line);
                    },
                },
            });
        }
        catch (e) {
            throw new Error(mapSipMediaException(e));
        }
        const notifyResult = await referAwait.waitForReferNotify();
        if (notifyResult.outcome === 'failure') {
            throw new Error(notifyResult.message);
        }
        if (notifyResult.outcome === 'timeout') {
            setSipError('Transfer status timed out — the call may still be active.');
            return;
        }
        await hangupActiveOrDeclineIncoming();
    }, [
        account,
        conversationUi?.sipState,
        hangupActiveOrDeclineIncoming,
        mgrRef,
        conversationSessionRef,
        consultSessionRef,
        setSipError,
    ]);
    const startWarmTransfer = useCallback(async (rawNumber) => {
        const sm = mgrRef.current;
        const customer = conversationSessionRef.current;
        if (!sm) {
            throw new Error('Softphone not ready.');
        }
        if (!sm.isConnected()) {
            throw new Error('SIP disconnected.');
        }
        if (incomingRingRef.current) {
            throw new Error('Answer or decline the ringing call before transferring.');
        }
        if (!customer || customer.state !== SessionState.Established) {
            throw new Error('Answer the customer call before warm transfer.');
        }
        if (consultSessionRef.current !== null || warmConsultDialAllowedRef.current) {
            throw new Error('Warm transfer is already in progress.');
        }
        const trimmed = rawNumber.trim();
        let destUri;
        try {
            destUri = sipDestinationUri(trimmed, account);
        }
        catch {
            throw new Error('Enter a destination number or extension.');
        }
        warmConsultDialAllowedRef.current = true;
        lastOutboundDialRef.current = trimmed;
        setWarmTransfer({ phase: 'consult_ringing', destinationLabel: trimmed });
        setSipError(null);
        try {
            await sm.hold(customer);
            await sm.call(destUri, OUTBOUND_INVITER_OPTIONS, {
                requestDelegate: {
                    onProgress: (response) => {
                        handleOutboundEarlyMediaProgress(response, consultSessionRef.current ?? conversationSessionRef.current, sipAudioElement);
                    },
                    onReject: (response) => {
                        setSipError(sipResponseLine('Consult call rejected', response));
                    },
                },
            });
        }
        catch (e) {
            warmConsultDialAllowedRef.current = false;
            consultSessionRef.current = null;
            setWarmTransfer({ phase: 'idle', destinationLabel: null });
            if (sm.isHeld(customer)) {
                await sm.unhold(customer).catch(() => undefined);
            }
            const msg = e instanceof Error ? e.message : mapSipMediaException(e);
            throw new Error(msg);
        }
    }, [
        account,
        sipAudioElement,
        mgrRef,
        conversationSessionRef,
        incomingRingRef,
        consultSessionRef,
        warmConsultDialAllowedRef,
        lastOutboundDialRef,
        setWarmTransfer,
        setSipError,
    ]);
    const completeWarmTransfer = useCallback(async () => {
        const sm = mgrRef.current;
        const customer = conversationSessionRef.current;
        const consult = consultSessionRef.current;
        if (!sm) {
            throw new Error('Softphone not ready.');
        }
        if (!customer || !consult) {
            throw new Error('No warm-transfer consult leg.');
        }
        if (consult.state !== SessionState.Established) {
            throw new Error('Wait until the consult call is answered before completing transfer.');
        }
        setSipError(null);
        warmSkipRecoverOnConsultHangupRef.current = true;
        const referAwait = createReferAwaitOptions();
        try {
            await sm.transfer(customer, consult, {
                onNotify: referAwait.onNotify,
                requestDelegate: {
                    onReject: (response) => {
                        const line = sipResponseLine('Attended transfer rejected', response);
                        referAwait.cancelReferWait(line);
                        void setSipError(line);
                    },
                },
            });
        }
        catch (e) {
            warmSkipRecoverOnConsultHangupRef.current = false;
            throw new Error(mapSipMediaException(e));
        }
        const notifyResult = await referAwait.waitForReferNotify();
        if (notifyResult.outcome === 'failure') {
            warmSkipRecoverOnConsultHangupRef.current = false;
            throw new Error(notifyResult.message);
        }
        if (notifyResult.outcome === 'timeout') {
            warmSkipRecoverOnConsultHangupRef.current = false;
            setSipError('Attended transfer status timed out — review call state.');
            return;
        }
        setWarmTransfer({ phase: 'idle', destinationLabel: null });
        await hangupActiveOrDeclineIncoming();
    }, [
        mgrRef,
        conversationSessionRef,
        consultSessionRef,
        warmSkipRecoverOnConsultHangupRef,
        setSipError,
        setWarmTransfer,
        hangupActiveOrDeclineIncoming,
    ]);
    const cancelWarmTransfer = useCallback(async () => {
        const sm = mgrRef.current;
        const customer = conversationSessionRef.current;
        if (!sm) {
            consultSessionRef.current = null;
            warmConsultDialAllowedRef.current = false;
            setWarmTransfer({ phase: 'idle', destinationLabel: null });
            return;
        }
        if (warmConsultDialAllowedRef.current && !consultSessionRef.current && customer) {
            warmConsultDialAllowedRef.current = false;
            setWarmTransfer({ phase: 'idle', destinationLabel: null });
            if (sm.isHeld(customer)) {
                await sm.unhold(customer).catch(() => undefined);
            }
            return;
        }
        const consult = consultSessionRef.current;
        if (consult) {
            await sm.hangup(consult).catch(() => undefined);
        }
        setWarmTransfer({ phase: 'idle', destinationLabel: null });
    }, [mgrRef, conversationSessionRef, consultSessionRef, warmConsultDialAllowedRef, setWarmTransfer]);
    const performSupervisorCall = useCallback(async (action, parties) => {
        if (action === 'disconnect') {
            await hangupActiveOrDeclineIncoming();
            return;
        }
        const st = conversationUiRef.current?.sipState;
        const voiceBusy = incomingRingRef.current !== null ||
            consultSessionRef.current !== null ||
            (st !== undefined && st !== SessionState.Terminated);
        if (voiceBusy) {
            throw new Error('Finish or hang up the current voice session before starting supervision.');
        }
        let keypad;
        try {
            keypad = buildSupervisorDialString(action, parties);
        }
        catch (e) {
            const msg = e instanceof Error ? e.message : String(e);
            throw new Error(msg);
        }
        await dial(keypad);
    }, [
        dial,
        hangupActiveOrDeclineIncoming,
        conversationUiRef,
        incomingRingRef,
        consultSessionRef,
    ]);
    const sendDtmf = useCallback(async (tone) => {
        const sm = mgrRef.current;
        const session = conversationSessionRef.current;
        const st = conversationUiRef.current?.sipState;
        if (!sm || !session || st !== SessionState.Established) {
            throw new Error('No active call to send DTMF.');
        }
        const digit = tone.trim();
        if (!/^[0-9A-D#*,]$/.test(digit)) {
            throw new Error(`Invalid DTMF tone: ${tone}`);
        }
        await sm.sendDTMF(session, digit);
    }, [mgrRef, conversationSessionRef, conversationUiRef]);
    return {
        dial,
        declineIncoming,
        answerIncoming,
        hangupActiveOrDeclineIncoming,
        toggleMute,
        toggleHold,
        transferActiveCall,
        startWarmTransfer,
        completeWarmTransfer,
        cancelWarmTransfer,
        performSupervisorCall,
        sendDtmf,
    };
}
